/**
 * Plan & product performance (Analytics spec §3.3) — every plan as a row ranked
 * by revenue, with units sold, active base, avg tenure and still-active % side by
 * side (so "what sells" vs "what's worth it" is visible). Plus session-pack
 * burn-down. Reads 0147 views. Owner/manager. Backend data, read-only.
 */
import { getLtvByPlan, getPlanPerformance, getSessionPackBurndown } from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import { Package } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandableCard } from '../../components/ui/ExpandableCard.js';
import { EmptyState } from '../../components/ui/Feedback.js';
import { Money, useFormatters } from '../../components/ui/Money.js';
import { Chip } from '../../components/ui/StatusPill.js';
import { Table, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

export function PlanPerformance(): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const on = { enabled: !!tenantId };

  const plans = useQuery({
    queryKey: ['an_plan_perf', tenantId],
    queryFn: () => getPlanPerformance(supabase, tenantId),
    ...on,
  });
  const packs = useQuery({
    queryKey: ['an_packs', tenantId],
    queryFn: () => getSessionPackBurndown(supabase, tenantId),
    ...on,
  });
  const ltv = useQuery({
    queryKey: ['an_ltv_plan', tenantId],
    queryFn: () => getLtvByPlan(supabase, tenantId),
    ...on,
  });

  // LTV is computed in its own view; index by plan so it sits beside units sold
  // ("what sells" vs "what's worth it" — the §3.3 headline).
  const ltvByPlan = useMemo(
    () => new Map((ltv.data ?? []).map((r) => [r.plan_id, r.ltv])),
    [ltv.data],
  );

  return (
    <div className="grid gap-5">
      <ExpandableCard
        title={t('analytics.intel.planPerformance')}
        subtitle={t('analytics.intel.planSubtitle')}
        loading={plans.isLoading}
        empty={(plans.data ?? []).length === 0 ? t('common.noData') : undefined}
      >
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>{t('memberships.planName')}</TH>
                <TH numeric>{t('analytics.intel.unitsSold')}</TH>
                <TH numeric>{t('analytics.intel.revenue')}</TH>
                <TH numeric>{t('analytics.intel.activeBase')}</TH>
                <TH numeric>{t('analytics.intel.avgTenureDays')}</TH>
                <TH numeric>{t('analytics.intel.stillActivePct')}</TH>
                <TH numeric>{t('analytics.intel.ltvCol')}</TH>
              </tr>
            </THead>
            <tbody>
              {(plans.data ?? []).map((p) => (
                <TR key={p.plan_id}>
                  <TD className="font-medium">
                    {p.plan_name}{' '}
                    <Chip tone="neutral">
                      {t(`plans.type.${p.plan_type}`, { defaultValue: p.plan_type })}
                    </Chip>
                  </TD>
                  <TD numeric>{fmt.number(p.units_sold)}</TD>
                  <TD numeric>
                    <Money minor={p.revenue} />
                  </TD>
                  <TD numeric className="text-muted">
                    {fmt.number(p.active_members)}
                  </TD>
                  <TD numeric className="text-muted">
                    {p.avg_tenure_days}
                  </TD>
                  <TD numeric>
                    <Chip
                      tone={
                        (p.still_active_pct ?? 0) >= 50
                          ? 'success'
                          : (p.still_active_pct ?? 0) >= 25
                            ? 'warning'
                            : 'danger'
                      }
                    >
                      {p.still_active_pct ?? 0}%
                    </Chip>
                  </TD>
                  <TD numeric>
                    {ltvByPlan.get(p.plan_id) != null ? (
                      <Money minor={ltvByPlan.get(p.plan_id) as number} />
                    ) : (
                      '—'
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ExpandableCard>

      <ExpandableCard
        title={t('analytics.intel.packBurndown')}
        subtitle={t('analytics.intel.packSubtitle')}
        loading={packs.isLoading}
        empty={(packs.data ?? []).length === 0 ? t('analytics.intel.noPacks') : undefined}
      >
        {(packs.data ?? []).length === 0 ? (
          <EmptyState icon={Package} title={t('analytics.intel.noPacks')} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>{t('memberships.planType')}</TH>
                  <TH numeric>{t('analytics.intel.creditsUsed')}</TH>
                  <TH numeric>{t('analytics.intel.creditsTotal')}</TH>
                  <TH numeric>{t('analytics.intel.pctUsed')}</TH>
                </tr>
              </THead>
              <tbody>
                {(packs.data ?? []).slice(0, 50).map((p) => (
                  <TR key={p.membership_id}>
                    <TD>{t(`plans.type.${p.plan_type}`, { defaultValue: p.plan_type })}</TD>
                    <TD numeric>{p.credits_used}</TD>
                    <TD numeric className="text-muted">
                      {p.credits_total ?? '—'}
                    </TD>
                    <TD numeric>
                      <Chip
                        tone={
                          (p.pct_used ?? 0) >= 80
                            ? 'danger'
                            : (p.pct_used ?? 0) >= 50
                              ? 'warning'
                              : 'neutral'
                        }
                      >
                        {p.pct_used ?? 0}%
                      </Chip>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </ExpandableCard>
    </div>
  );
}
